import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { studentService } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const ParentResults: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [results, setResults] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<string>('');

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchResults();
    }
  }, [selectedStudent]);

  const fetchStudents = async () => {
    try {
      const response = await studentService.getAll();
      const parentStudents = response.data.students || [];
      setStudents(parentStudents);
      if (parentStudents.length > 0) {
        setSelectedStudent(parentStudents[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await studentService.getAll();
      const studentResults = response.data.students?.find((s: any) => s.id === selectedStudent)?.results || [];
      setResults(studentResults);
    } catch (error) {
      console.error('Failed to fetch results:', error);
    }
  };

  if (loading) {
    return <Layout title="Children's Results"><div className="text-center py-8">Loading...</div></Layout>;
  }

  const currentStudent = students.find(s => s.id === selectedStudent);
  const averageMarks = results.length > 0 
    ? (results.reduce((sum, r) => sum + (r.marksObtained || 0), 0) / results.length).toFixed(2)
    : '0';

  return (
    <Layout title="Children's Results">
      <div className="mb-6">
        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Select Child</label>
        <select
          value={selectedStudent}
          onChange={(e) => setSelectedStudent(e.target.value)}
          className={`w-full md:w-64 border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
        >
          {students.map((student) => (
            <option key={student.id} value={student.id}>
              {student.user?.firstName} {student.user?.lastName} (Class {student.class?.name})
            </option>
          ))}
        </select>
      </div>

      {/* Summary Card */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 mb-6`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Performance Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Average Marks</p>
            <p className="text-2xl font-bold text-blue-600">{averageMarks}</p>
          </div>
          <div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Exams</p>
            <p className="text-2xl font-bold text-green-600">{results.length}</p>
          </div>
          <div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Subjects</p>
            <p className="text-2xl font-bold text-purple-600">{new Set(results.map(r => r.subject?.id)).size}</p>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
        <table className="w-full">
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
            <tr>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Subject</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Exam Type</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Marks</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Grade</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {results.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">No results available</td>
              </tr>
            ) : (
              results.map((result, index) => {
                const percentage = result.totalMarks ? ((result.marksObtained / result.totalMarks) * 100).toFixed(2) : 0;
                return (
                  <tr key={index} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{result.subject?.name || 'Unknown'}</td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{result.examType || 'N/A'}</td>
                    <td className={`px-6 py-4 font-semibold text-blue-600`}>{result.marksObtained || 0}</td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{result.totalMarks || 0}</td>
                    <td className={`px-6 py-4 font-semibold text-purple-600`}>{result.grade || 'N/A'}</td>
                    <td className={`px-6 py-4`}>
                      <span className={`text-sm font-semibold ${
                        parseFloat(percentage as string) >= 80 ? 'text-green-600' :
                        parseFloat(percentage as string) >= 60 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {percentage}%
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
};

export default ParentResults;
